import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { AdsTable } from './ads-table';
import { useSocialAdsTopAdsTable } from './hooks/use-social-ads-top-ads-table';
import { adsTableColumns } from './ads-table-columns';
import { sharedRenderRow } from './shared-render-row';
import type { TopAdData } from '../../hooks/use-social-ads-overview-tab';
import { ADS_ROW_TYPE, type AdsRowType } from '../../social-ads-top-ads';

// Map accessorKey to AdsRowType
const mapAccessorKeyToRowType = (accessorKey: string): AdsRowType => {
  switch (accessorKey) {
    case 'by.name':
      return ADS_ROW_TYPE.AD_NAME;
    case 'by.id':
      return ADS_ROW_TYPE.SOCIAL_ACCOUNT;
    case 'metrics.roas':
      return ADS_ROW_TYPE.ROAS;
    case 'metrics.ctr':
      return ADS_ROW_TYPE.PERCENTAGE;
    case 'metrics.impressions':
    case 'metrics.reach':
    case 'metrics.clicks':
      return ADS_ROW_TYPE.NUMBER;
    case 'metrics.spend.amount':
    case 'metrics.purchase.amount':
    case 'metrics.cpm':
    case 'metrics.cpc':
      return ADS_ROW_TYPE.CURRENCY;
    default:
      return ADS_ROW_TYPE.DEFAULT;
  }
};

interface SocialAdsOverviewTableProps {
  data: TopAdData[];
  isLoading?: boolean;
  isRefreshing?: boolean;
  hideAdsTableHeader?: boolean;
}

export function SocialAdsOverviewTable({
  data,
  isLoading = false,
  isRefreshing = false,
  hideAdsTableHeader = false,
}: SocialAdsOverviewTableProps) {
  const { adsTableProps } = useSocialAdsTopAdsTable({ data, isLoading, isRefreshing });

  const columns = useMemo((): ColumnDef<TopAdData>[] => {
    return adsTableColumns.map((col) => {
      const accessorKey = (col as ColumnDef<TopAdData> & { accessorKey: string }).accessorKey;
      const id = col.id ?? accessorKey;
      return {
        ...col,
        id,
        // Remove any tooltip property from meta to prevent DataTable from rendering tooltips
        meta: {
          ...col.meta,
          tooltip: undefined, // Explicitly remove tooltip to prevent overlay issues
        },
        header: () => (
          <span
            className={[
              'flex',
              [
                'metrics.impressions',
                'metrics.reach',
                'metrics.clicks',
                'metrics.spend.amount',
                'metrics.purchase.amount',
                'metrics.cpm',
                'metrics.ctr',
                'metrics.cpc',
              ].includes(accessorKey)
                ? 'justify-end'
                : '',
              'font-medium text-gray-900 text-[14px]',
            ].join(' ')}
          >
            {col.header}
          </span>
        ),
        cell: ({ row }) => {
          if (isRefreshing) {
            return <div className="h-4 rounded bg-gray-100 animate-pulse w-full" />;
          }
          const rowType = mapAccessorKeyToRowType(accessorKey);
          return sharedRenderRow(rowType, row, { ...col, id: accessorKey });
        },
      };
    });
  }, [isRefreshing]);

  return <AdsTable {...adsTableProps} columns={columns} hideAdsTableHeader={hideAdsTableHeader} />;
}
